import React, { useState, useEffect } from 'react';
import './App.css';
import { CanliDovizService } from './services/canliDovizService';
import { CryptoService } from './services/cryptoService';
import { BISTPrice, CurrencyPrice, GoldPrice, CryptoPrice, TabType } from './services/types';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthWrapper from './components/AuthWrapper';
import Dashboard from './components/Dashboard';

const AppContent: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>(() => window.location.hash === '#payments' ? 'payments' : 'bist');
  const [stocks, setStocks] = useState<BISTPrice[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyPrice[]>([]);
  const [golds, setGolds] = useState<GoldPrice[]>([]);
  const [cryptos, setCryptos] = useState<CryptoPrice[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    const openPayments = () => {
      if (window.location.hash === '#payments') setActiveTab('payments');
    };
    window.addEventListener('hashchange', openPayments);
    return () => window.removeEventListener('hashchange', openPayments);
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    // Retry mekanizması ile veri yükleme
    const loadDataWithRetry = async (retries = 3): Promise<void> => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Canlı Döviz'den tüm verileri çek
          const canliDovizService = new CanliDovizService();
          
          const cryptoService = new CryptoService();
          const [bistData, currencyData, goldData, cryptoData] = await Promise.all([
            canliDovizService.fetchBISTData(),
            canliDovizService.fetchCurrencyDataFromCanliDoviz(),
            canliDovizService.fetchGoldDataFromCanliDoviz(),
            cryptoService.fetchCryptoData(),
          ]);
          // Remove duplicate stocks by StockCode and filter null values
          const uniqueStocks = bistData
            .filter((stock: BISTPrice) => stock && stock.StockCode) // Filter out null/undefined stocks
            .filter((stock: BISTPrice, index: number, self: BISTPrice[]) => 
              index === self.findIndex((s: BISTPrice) => s.StockCode === stock.StockCode)
            );
          
          setStocks(uniqueStocks);
          setCurrencies(currencyData);
          setGolds(goldData);
          setCryptos(cryptoData);
          
          if (isFirstLoad) {
            setIsFirstLoad(false);
          }
          
          return; // Başarılı olursa fonksiyondan çık
          
        } catch (error) {
          console.error(`❌ Attempt ${attempt} failed:`, error);
          
          if (attempt === retries) {
            console.error('🚨 All retry attempts failed. Please check if the server is running.');
            if (isFirstLoad) {
                setIsFirstLoad(false);
            }
            return;
          }
          
          // Sonraki deneme için bekle
          const waitTime = attempt * 2000; // 2s, 4s, 6s
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    };

    // İlk yükleme
    loadDataWithRetry();

    // Her 30 saniyede bir verileri güncelle (arka planda, hiçbir UI değişikliği olmadan)
    const interval = setInterval(() => {
      loadDataWithRetry(1); // Arka planda sadece 1 kez dene
    }, 30000); // 30 saniye

    // Component unmount olduğunda interval'i temizle
    return () => clearInterval(interval);
  }, [authLoading, isAuthenticated, isFirstLoad, stocks.length]);

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Show auth forms if not authenticated
  if (!isAuthenticated) {
    return <AuthWrapper />;
  }

  return (
    <Dashboard
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      stocks={stocks}
      currencies={currencies}
      golds={golds}
      cryptos={cryptos}
    />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
